import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeadActivity } from '../services/api';
import { alertsOn } from '../components/BrowserAlertsSetting';

// One poll owns "new activity" (see CONTEXT.md). Mount <LiveActivityProvider>
// once while staff are signed in; screens call useLiveActivity() and refetch
// when `activityVersion` changes. `events` holds the latest batch so alerts
// (tickets #47, #48) can build on the same poll.
export const POLL_INTERVAL_MS = 30000;

const LiveActivityContext = createContext({
  events: [],
  newCount: 0,
  activityVersion: 0,
  clearNewCount: () => {},
});

export const useLiveActivity = () => useContext(LiveActivityContext);

const isLooking = () => !document.hidden && document.hasFocus();

// Kinds are generic on purpose: unknown kinds still get readable text.
const KIND_TEXT = {
  new_lead: 'New lead',
  inbound_text: 'New text',
  repeat_submission: 'New submission',
  call: 'New call',
};

// One alert per poll batch. Returns {title, body, path} for the alert or toast.
const describeBatch = (events) => {
  if (events.length === 1) {
    const [e] = events;
    return {
      title: e.lead_name || 'A lead',
      body: KIND_TEXT[e.kind] || 'New activity',
      path: `/leads/${e.lead_id}`,
    };
  }
  return {
    title: `${events.length} new events`,
    body: 'Click to see the unread leads',
    path: '/leads?is_read_by_user=false',
  };
};

export const LiveActivityProvider = ({ enabled = true, children }) => {
  const [events, setEvents] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [activityVersion, setActivityVersion] = useState(0);
  const [toast, setToast] = useState(null);
  const cursor = useRef(null);
  const baseTitle = useRef(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const open = (path) => {
    window.focus();
    navigateRef.current(path);
  };

  // Desktop alert when allowed; otherwise a toast.
  const announce = (batch) => {
    if (!alertsOn()) return;
    const { title, body, path } = describeBatch(batch);
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const n = new Notification(title, { body });
      n.onclick = () => {
        open(path);
        if (n.close) n.close();
      };
    } else {
      setToast({ title, body, path });
    }
  };
  const announceRef = useRef(announce);
  announceRef.current = announce;

  const clearNewCount = useCallback(() => setNewCount(0), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;

    const poll = async () => {
      try {
        const data = await getLeadActivity(cursor.current);
        if (stopped) return;
        const first = cursor.current === null;
        cursor.current = data.since;
        // The first poll only sets the starting point.
        if (first || !data.events.length) return;
        setEvents(data.events);
        setActivityVersion((v) => v + 1);
        announceRef.current(data.events);
        // Staff already looking at the screen see the refresh; no count needed.
        if (alertsOn() && !isLooking()) setNewCount((n) => n + data.events.length);
      } catch (err) {
        // Keep the old cursor and try again next tick.
        console.error('Activity poll failed:', err);
      }
    };

    // Hidden tabs poll every other tick (about every 60s).
    let skipNext = false;
    const tick = () => {
      if (document.hidden) {
        if (skipNext) {
          skipNext = false;
          return;
        }
        skipNext = true;
      } else {
        skipNext = false;
      }
      poll();
    };

    poll();
    const timer = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [enabled]);

  // Tab title: "(3) Dentara". The count clears when staff look at the tab.
  useEffect(() => {
    if (baseTitle.current === null) baseTitle.current = document.title;
    document.title = newCount ? `(${newCount}) ${baseTitle.current}` : baseTitle.current;
  }, [newCount]);

  // The toast fades after 8 seconds.
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const onLook = () => {
      if (isLooking()) setNewCount(0);
    };
    document.addEventListener('visibilitychange', onLook);
    window.addEventListener('focus', onLook);
    return () => {
      document.removeEventListener('visibilitychange', onLook);
      window.removeEventListener('focus', onLook);
    };
  }, []);

  return (
    <LiveActivityContext.Provider value={{ events, newCount, activityVersion, clearNewCount }}>
      {children}
      {toast && (
        <button
          type="button"
          role="status"
          onClick={() => {
            open(toast.path);
            setToast(null);
          }}
          className="fixed bottom-4 right-4 z-[300] rounded-lg bg-teal-700 px-4 py-3 text-left text-sm text-white shadow-lg"
        >
          <span className="block font-medium">{toast.title}</span>
          <span className="block">{toast.body}</span>
        </button>
      )}
    </LiveActivityContext.Provider>
  );
};
